import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import http from "@/api";
import { MARKETPLACE_ENABLED } from "@/flags";
import { useApp } from "@/context";
import ProductCard from "@/components/ProductCard";
import Seo from "@/components/Seo";
import LocationPicker from "@/components/LocationPicker";
import SpinWheel from "@/components/SpinWheel";
import LiveBanner from "@/components/LiveBanner";
import TodaysOffers from "@/components/TodaysOffers";
import HeroCarousel from "@/components/home/HeroCarousel";
import HomeDepartments from "@/components/home/HomeDepartments";
import HomeGifts from "@/components/home/HomeGifts";
import AppFeatures from "@/components/home/AppFeatures";
import BloggersRow from "@/components/home/BloggersRow";

/* الرئيسية بالتصميم المعتمد من المستخدم:
   بحث → بنر متحرك بنقاط → شبكة أقسام 4×3 بصفحات → الأكثر مبيعاً → العروض → معرض التصاميم */
export default function Home() {
  const { lang, country, branchId } = useApp();
  const navigate = useNavigate();
  const ar = lang === "ar";
  const [homeQ, setHomeQ] = useState("");
  const [best, setBest] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasDesigns, setHasDesigns] = useState(false);

  const submitSearch = (e) => {
    e.preventDefault();
    const term = homeQ.trim();
    navigate(term.length >= 2 ? `/shop?q=${encodeURIComponent(term)}` : "/shop");
  };

  useEffect(() => {
    (async () => {
      try {
        const _b = localStorage.getItem("rg_branch_id") || "";
        const _deptQ = `/departments?country=${localStorage.getItem("rg_country") || "QA"}${_b ? `&branch_id=${_b}` : ""}`;
        const [{ data: products }, { data: depts }] = await Promise.all([
          http.get("/products?limit=10&sort=bestselling"),
          http.get(_deptQ),
        ]);
        setBest(products || []);
        setDepartments(depts || []);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
      http.get("/ai/designs/gallery?limit=1")
        .then(({ data }) => setHasDesigns((data.count || 0) > 0)).catch(() => {});
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, branchId]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 space-y-4 sm:space-y-6">
      <Seo />

      <LocationPicker />
      <SpinWheel />
      <LiveBanner />

      {/* البحث */}
      <form onSubmit={submitSearch} className="relative" data-testid="home-search-form">
        <Search size={17} className="absolute top-1/2 -translate-y-1/2 opacity-50"
                style={{ [ar ? "right" : "left"]: 16 }} />
        <input
          className="input-field !py-3 !rounded-full shadow-sm"
          style={{ [ar ? "paddingRight" : "paddingLeft"]: "2.8rem" }}
          placeholder={ar ? "ابحث عن ورد، هدايا، مناسبات ..." : "Search flowers, gifts, occasions…"}
          value={homeQ}
          onChange={(e) => setHomeQ(e.target.value)}
          data-testid="home-search-input"
        />
        <button type="submit" className="sr-only" data-testid="home-search-submit">
          {ar ? "بحث" : "Search"}
        </button>
      </form>

      {/* البنر الرئيسي المتحرك */}
      <HeroCarousel />

      {/* شبكة الأقسام المخففة: صفّان × 5 — أبرز 9 + «الكل» */}
      {loading && !departments.length ? (
        <div className="grid grid-cols-5 gap-2" data-testid="layout-skeleton">
          {Array.from({ length: 10 }, (_, i) => (
            <div key={i} className="aspect-[4/5] skeleton rounded-2xl" />
          ))}
        </div>
      ) : (
        <HomeDepartments departments={departments} />
      )}

      {/* 🎁 الهدايا من RG — أسفل صفوف الزهور مباشرة */}
      <HomeGifts />

      {/* مميزات ستوديو الذكاء — قسم تسويقي بارز مكان العروض */}
      <AppFeatures />

      {/* 🎂 مصمم الكيك بالذكاء */}
      <Link to="/cake-designer" className="rounded-2xl p-4 flex items-center gap-3 flex-wrap card-soft"
            style={{ background: "linear-gradient(135deg, rgba(198,168,124,0.14), rgba(17,57,42,0.07))" }}
            data-testid="home-cake-banner">
        <span style={{ fontSize: 28 }}>🎂</span>
        <div className="flex-1 min-w-[150px]">
          <div className="font-bold text-sm">{ar ? "صمّم كيكتك وشاهدها قبل الطلب ✨" : "Design your cake & see it before ordering ✨"}</div>
          <div className="text-[11px] opacity-60">
            {ar ? "اختر الشكل والنكهة والألوان — الذكاء يصمم لك 3 كيكات واقعية وتعدّلها بكلمة" : "Pick shape, flavor & colors — AI designs 3 real cakes you can tweak"}
          </div>
        </div>
        <span className="btn-primary !min-h-0 !py-2 !px-4 !text-[12px]">{ar ? "جرّب الآن" : "Try now"}</span>
      </Link>

      {/* 🌿 صمّم حديقتك بالذكاء */}
      <Link to="/garden-designer" className="relative rounded-2xl overflow-hidden block card-soft !p-0"
            data-testid="home-garden-banner">
        <img src="/img/garden-banner.jpg" alt={ar ? "صمّم حديقتك" : "Design your garden"}
             className="w-full h-36 sm:h-44 object-cover" loading="lazy" />
        <div className="absolute inset-0 flex items-center"
             style={{ background: ar
               ? "linear-gradient(to left, rgba(10,40,25,0.78) 0%, rgba(10,40,25,0.25) 55%, transparent 100%)"
               : "linear-gradient(to right, rgba(10,40,25,0.78) 0%, rgba(10,40,25,0.25) 55%, transparent 100%)" }}>
          <div className="px-4 sm:px-6 max-w-[62%] text-white space-y-1">
            <div className="font-bold text-sm sm:text-lg leading-snug">
              {ar ? "صمّم حديقتك بالذكاء 🌿" : "Design your garden with AI 🌿"}
            </div>
            <div className="text-[10px] sm:text-xs opacity-85 leading-snug">
              {ar ? "صوّرها → 3 تصاميم واقعية بنباتات تناسب مناخك → اشترِ النباتات بضغطة"
                  : "Photo → 3 realistic designs with climate-fit plants → buy in one tap"}
            </div>
            <span className="inline-block mt-1 text-[11px] sm:text-xs font-bold px-4 py-1.5 rounded-full"
                  style={{ background: "var(--secondary, #c6a87c)", color: "#11392a" }}>
              {ar ? "جرّب مجاناً" : "Try free"}
            </span>
          </div>
        </div>
      </Link>

      {/* عروض اليوم — قبل الأكثر مبيعاً */}
      <TodaysOffers />

      {/* 🛍 اختيارات البلوقرز — يظهر فقط عند وجود قوائم معتمدة */}
      <BloggersRow />

      {/* الأكثر مبيعاً */}
      {best.length > 0 && (
        <section className="space-y-2.5" data-testid="home-bestsellers">
          <div className="flex items-center justify-between">
            <h2 className={`${ar ? "font-display-ar" : "font-display-en"} text-xl sm:text-2xl font-bold`}>
              {ar ? "الأكثر مبيعاً" : "Best sellers"}
            </h2>
            <Link to="/shop?sort=bestselling" className="text-[13px] font-bold" style={{ color: "#2563eb" }}
                  data-testid="home-bestsellers-all">
              {ar ? "‹ عرض الكل" : "View all ›"}
            </Link>
          </div>
          <div className="flex gap-3 overflow-x-auto scroll-x-hide -mx-4 px-4 sm:mx-0 sm:px-0 pb-1 snap-x">
            {best.map((p) => (
              <div key={p.id} className="shrink-0 snap-start" style={{ width: "min(46vw, 220px)" }}>
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* معرض تصاميم العملاء — يظهر فقط عند وجود تصاميم مشتركة */}
      {hasDesigns && (
        <Link to="/designs" className="rounded-2xl p-4 flex items-center gap-3 flex-wrap card-soft"
              style={{ background: "rgba(16,84,56,0.06)" }} data-testid="home-designs-banner">
          <span style={{ fontSize: 24 }}>🎨</span>
          <div className="flex-1 min-w-[150px]">
            <div className="font-bold text-sm">{ar ? "معرض تصاميم عملائنا" : "Customer designs gallery"}</div>
            <div className="text-[11px] opacity-60">
              {ar ? "تصاميم حقيقية بالذكاء صمّمها عملاؤنا — استلهم واطلب بنفس الشكل" : "Real AI designs by our customers — get inspired"}
            </div>
          </div>
          <span className="btn-primary !min-h-0 !py-2 !px-4 !text-[12px]">{ar ? "شاهد المعرض" : "View gallery"}</span>
        </Link>
      )}

      {/* المتاجر الشريكة (معطل عبر flags.js) */}
      {MARKETPLACE_ENABLED && (
        <div className="rounded-2xl p-4 flex items-center gap-3 flex-wrap card-soft"
             style={{ background: "rgba(0,0,0,0.04)" }} data-testid="home-stores-banner">
          <span style={{ fontSize: 24 }}>🏬</span>
          <div className="flex-1 min-w-[150px]">
            <div className="font-bold text-sm">{ar ? "المتاجر الشريكة" : "Partner stores"}</div>
          </div>
          <Link to="/stores" className="btn-primary !min-h-0 !py-2 !px-4 !text-[12px]" data-testid="home-stores-btn">
            {ar ? "تصفح المتاجر" : "Browse stores"}
          </Link>
        </div>
      )}
    </div>
  );
}
